import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { sessionRequest, createAdminApi } from '../lib/admin-api';
import { useAbortableTask } from '../hooks/useAbortableTask';

const AdminSessionContext = createContext(null);

export const AdminSessionProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const task = useAbortableTask();
  const identity = useRef(null);
  const expire = useCallback(expected => {
    if (expected && identity.current !== expected) return;
    identity.current = null; setSession(null); setStatus('anonymous');
  }, []);
  const applySession = useCallback(data => {
    identity.current = data.csrf_token; setSession(data); setStatus('authenticated'); setError('');
  }, []);
  const refresh = useCallback(async () => {
    const operation = task.start();
    try {
      const data = await sessionRequest('/session', { signal: operation.signal });
      if (task.isCurrent(operation)) applySession(data);
    } catch (failure) {
      if (!task.isCurrent(operation)) return;
      if (failure.status === 401) expire();
      else { setError(failure.message); setStatus('error'); }
    }
  }, [task, applySession, expire]);
  useEffect(() => {
    localStorage.removeItem('lawyah_admin_token'); // Remove the invalidated legacy credential; never store new secrets here.
    refresh();
    window.addEventListener('focus', refresh);
    return () => { window.removeEventListener('focus', refresh); task.cancel(); };
  }, [refresh, task]);
  useEffect(() => {
    if (!session) return undefined;
    const timer = window.setTimeout(() => expire(session.csrf_token), Math.max(0, new Date(session.expires_at).getTime() - Date.now()));
    return () => window.clearTimeout(timer);
  }, [session, expire]);
  const login = useCallback(async password => {
    const operation = task.start();
    const data = await sessionRequest('/login', { method: 'POST', signal: operation.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
    if (task.isCurrent(operation)) applySession(data);
  }, [applySession, task]);
  const logout = useCallback(async () => {
    const operation = task.start();
    try { await sessionRequest('/logout', { method: 'POST', signal: operation.signal, headers: { 'X-CSRF-Token': session?.csrf_token || '' } }); }
    catch (failure) { if (failure.status !== 401) throw failure; }
    if (task.isCurrent(operation)) expire(session?.csrf_token);
  }, [session, task, expire]);
  const api = useMemo(() => createAdminApi(session?.csrf_token, () => expire(session?.csrf_token)), [session?.csrf_token, expire]);
  const value = useMemo(() => ({ session, status, error, login, logout, refresh, api }), [session, status, error, login, logout, refresh, api]);
  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
};

export function useAdminSession() {
  const value = useContext(AdminSessionContext);
  if (!value) throw new Error('Admin session provider is required');
  return value;
}